import { splitProps, type Component, type JSX } from 'solid-js';

type ButtonVariant = 'default' | 'destructive';

// Height. 'default' is the standalone button and the one to reach for. Inside a
// row that carries its own hairline, a button asserting a fixed height overflows
// it and pushes the row a pixel taller, so there use 'fill' to take whatever is
// actually available, or 'sm' to stay clear of it.
type ButtonSize = 'default' | 'fill' | 'sm';

const baseClasses = 'inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-foreground disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0';

const variantClasses: Record<ButtonVariant, string> = {
    default: 'hover:bg-muted',
    destructive: 'bg-red text-background hover:bg-red/90',
};

// Shape is auto-detected, not a prop: an icon on its own renders as a square;
// anything with a label (or custom children) gets the padded size.
const iconSize: Record<ButtonSize, string> = {
    default: 'h-7 w-7 rounded',
    fill: 'h-full aspect-square rounded',
    sm: 'h-5 w-5 rounded',
};

const labelSize: Record<ButtonSize, string> = {
    default: 'h-7 p-2 px-1.5 rounded',
    fill: 'h-full px-1.5 rounded',
    sm: 'h-5 px-1.5 rounded',
};

function mergeClasses(...classes: (string | undefined)[]): string {
    return classes.filter(Boolean).join(' ');
}

export interface ButtonProps {
    variant?: ButtonVariant;
    size?: ButtonSize;
    icon?: JSX.Element;
    label?: JSX.Element;
    class?: string;
    // Escape hatch for buttons whose content is richer than icon/label
    // (stat pills, swatch legends, Show-wrapped tooltips, etc.).
    children?: JSX.Element;
    ref?: HTMLButtonElement | ((el: HTMLButtonElement) => void);
    [key: string]: any;
}

const Button: Component<ButtonProps> = (props) => {
    const [local, others] = splitProps(props, ['class', 'variant', 'size', 'icon', 'label', 'ref', 'children']);

    const variant = () => local.variant ?? 'default';
    const size = () => local.size ?? 'default';
    const iconOnly = () => local.icon != null && local.label == null && local.children == null;

    // Use a getter function to make classes reactive
    const classes = () => mergeClasses(baseClasses, variantClasses[variant()], (iconOnly() ? iconSize : labelSize)[size()], local.class);

    return (
        <button class={classes()} ref={local.ref} {...others}>
            {local.icon}
            {local.label}
            {local.children}
        </button>
    );
};

export { Button };
